/**
 * ThreatVisualization Engine
 * Shared utilities and data extraction for threat visualization
 */

export const NODE_TYPES = {
  ORG: 'org',
  ASSET: 'asset',
  TECH: 'tech',
  CVE: 'cve',
  CWE: 'cwe',
  CAPEC: 'capec',
  TTP: 'ttp',
  APT: 'apt',
};

export const NODE_COLORS = {
  [NODE_TYPES.ORG]: '#6366f1',
  [NODE_TYPES.ASSET]: '#10b981',
  [NODE_TYPES.TECH]: '#f97316',
  [NODE_TYPES.CVE]: '#e53935',
  [NODE_TYPES.CWE]: '#9c27b0',
  [NODE_TYPES.CAPEC]: '#00897b',
  [NODE_TYPES.TTP]: '#1e88e5',
  [NODE_TYPES.APT]: '#f59e0b',
};

export const RING_RADII = {
  [NODE_TYPES.ORG]: 0,
  [NODE_TYPES.ASSET]: 120,
  [NODE_TYPES.TECH]: 240,
  [NODE_TYPES.CVE]: 380,
  [NODE_TYPES.CWE]: 520,
  [NODE_TYPES.CAPEC]: 680,
  [NODE_TYPES.TTP]: 860,
  [NODE_TYPES.APT]: 1060,
};

export const NODE_SIZES = {
  [NODE_TYPES.ORG]: 40,
  [NODE_TYPES.ASSET]: 20,
  [NODE_TYPES.TECH]: 18,
  [NODE_TYPES.CVE]: 24,
  [NODE_TYPES.CWE]: 18,
  [NODE_TYPES.CAPEC]: 16,
  [NODE_TYPES.TTP]: 18,
  [NODE_TYPES.APT]: 24,
};

/**
 * Extract visualization data from insights
 * @param {Object} insightsData - The insights data object
 * @param {Array} selectedCveNames - Optional array of selected CVE names to filter
 * @param {String} assetNameOverride - Optional asset name to use instead of extracting from data
 * @param {Object} assetCorrelation - Optional asset correlation data from LastScanData
 */
export function extractVisualizationData(insightsData, selectedCveNames = null, assetNameOverride = null, assetCorrelation = null) {
  const result = {
    organization: null,
    assets: [],
    technologies: [],
    cves: [],
    cwes: [],
    capecs: [],
    ttps: [],
    aptGroups: [],
    // Relationship maps for linking
    orgToAssets: [],
    assetToTechs: {},
    techToCves: {},
    cveToWeaknesses: {},
    cweToCapecs: {},
    capecToTtps: {},
  };

  if (!insightsData) return result;

  // Get organization/asset name - use override if provided
  if (assetNameOverride) {
    result.organization = assetNameOverride;
  } else if (insightsData.organization?.name) {
    result.organization = insightsData.organization.name;
  } else {
    // Try to find a domain-like key in the data structure
    const keys = Object.keys(insightsData);
    const domainKey = keys.find(k => k.includes('.') && !k.startsWith('_'));
    result.organization = domainKey || 'Organization';
  }

  // Extract assets and technologies from correlation data
  if (assetCorrelation) {
    const { cveToAssets } = assetCorrelation;

    // Build asset and tech maps from cveToAssets (only vulnerable ones)
    const assetMap = new Map();
    const techMap = new Map();

    // Get the list of CVEs to process
    const orgCves = insightsData.organization?.org_cves || [];
    let cvesToProcess = orgCves;
    if (selectedCveNames && selectedCveNames.length > 0) {
      cvesToProcess = orgCves.filter(cve => selectedCveNames.includes(cve.name));
    }
    const selectedCveSet = new Set(cvesToProcess.map(c => c.name));

    // Process CVE to assets mapping to extract relevant assets and techs
    if (cveToAssets) {
      Object.entries(cveToAssets).forEach(([cveName, assets]) => {
        // Only process CVEs that are selected
        if (!selectedCveSet.has(cveName)) return;

        assets.forEach(assetData => {
          const { subdomain } = assetData;

          // Add asset if not already added
          if (!assetMap.has(subdomain)) {
            assetMap.set(subdomain, {
              id: subdomain,
              name: subdomain,
              type: NODE_TYPES.ASSET,
              hostIps: assetData.hostIps || [],
            });
            result.orgToAssets.push(subdomain);
          }

          // Add technology
          const techId = `${assetData.technology}/${assetData.technologyVersion}`;
          if (!techMap.has(techId)) {
            techMap.set(techId, {
              id: techId,
              name: assetData.technology,
              version: assetData.technologyVersion || 'N/A',
              type: NODE_TYPES.TECH,
              vulnerable: true,
            });
          }

          // Build asset to tech relationships
          if (!result.assetToTechs[subdomain]) {
            result.assetToTechs[subdomain] = [];
          }
          if (!result.assetToTechs[subdomain].includes(techId)) {
            result.assetToTechs[subdomain].push(techId);
          }

          // Build tech to CVE relationships
          if (!result.techToCves[techId]) {
            result.techToCves[techId] = [];
          }
          if (!result.techToCves[techId].includes(cveName)) {
            result.techToCves[techId].push(cveName);
          }
        });
      });
    }

    result.assets = Array.from(assetMap.values());
    result.technologies = Array.from(techMap.values());
  }

  // Extract CVEs with CWE/CAPEC data from organization.org_cves
  const orgCves = insightsData.organization?.org_cves || [];
  const cweMap = new Map();
  const capecMap = new Map();
  const ttpMap = new Map();

  let cvesToProcess = orgCves;
  // Filter by selected CVEs if provided
  if (selectedCveNames && selectedCveNames.length > 0) {
    cvesToProcess = orgCves.filter(cve => selectedCveNames.includes(cve.name));
  }

  result.cves = cvesToProcess.map(cve => {
    const cveId = cve.name;
    result.cveToWeaknesses[cveId] = [];

    // Process CWEs from the CVE
    if (cve.cwes && Array.isArray(cve.cwes)) {
      cve.cwes.forEach(cweData => {
        const cweId = cweData.name;
        if (!cweMap.has(cweId)) {
          cweMap.set(cweId, {
            id: cweId,
            name: cweId,
            capecs: [],
          });
        }
        result.cveToWeaknesses[cveId].push(cweId);

        // Process CAPECs from the CWE
        if (cweData.capec && Array.isArray(cweData.capec)) {
          if (!result.cweToCapecs[cweId]) {
            result.cweToCapecs[cweId] = [];
          }
          cweData.capec.forEach(capecData => {
            const capecId = capecData.name;
            if (!capecMap.has(capecId)) {
              capecMap.set(capecId, {
                id: capecId,
                name: capecId,
                ttps: [],
              });
            }
            if (!result.cweToCapecs[cweId].includes(capecId)) {
              result.cweToCapecs[cweId].push(capecId);
            }

            // Process TTPs from the CAPEC
            if (capecData.ttps && Array.isArray(capecData.ttps)) {
              if (!result.capecToTtps[capecId]) {
                result.capecToTtps[capecId] = [];
              }
              capecData.ttps.forEach(ttpData => {
                const ttpId = ttpData.id;
                if (ttpId && !ttpMap.has(ttpId)) {
                  ttpMap.set(ttpId, {
                    id: ttpId,
                    name: ttpData.name || ttpId,
                    description: ttpData.description,
                    tactics: ttpData.tactics || [],
                    maturity_level: ttpData.maturity_level,
                  });
                }
                if (ttpId && !result.capecToTtps[capecId].includes(ttpId)) {
                  result.capecToTtps[capecId].push(ttpId);
                }
              });
            }
          });
        }
      });
    }

    // Also process direct TTPs from CVE
    const directTtps = [];
    if (cve.ttps && Array.isArray(cve.ttps)) {
      cve.ttps.forEach(ttpData => {
        const ttpId = typeof ttpData === 'string' ? ttpData : ttpData.id;
        if (ttpId) {
          directTtps.push(ttpId);
          if (!ttpMap.has(ttpId)) {
            ttpMap.set(ttpId, {
              id: ttpId,
              name: typeof ttpData === 'string' ? ttpId : (ttpData.name || ttpId),
              description: typeof ttpData === 'object' ? ttpData.description : '',
              tactics: typeof ttpData === 'object' ? (ttpData.tactics || []) : [],
              maturity_level: typeof ttpData === 'object' ? ttpData.maturity_level : '',
            });
          }
        }
      });
    }

    return {
      id: cveId,
      name: cveId,
      ttps: directTtps,
      attackVector: cve.attack_vector_name,
      potentialRisk: cve.potential_risk,
      cvss: cve.cvss,
      cweCount: cve.cwes?.length || 0,
    };
  });

  // Convert maps to arrays
  result.cwes = Array.from(cweMap.values());
  result.capecs = Array.from(capecMap.values());
  result.ttps = Array.from(ttpMap.values());

  // Extract APT groups
  const groups = insightsData.groups || [];
  result.aptGroups = groups;

  return result;
}
